/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderIpAddrList, renderPageLink, renderSiteList } from '../../properties/Properties';
import { useCurrentFilterStateById } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import { stringProperty, listProperty, linkProperty, listLinkProperty } from '../../properties/funcs';
import { getUpTimeDuration } from '../../../utilities/DateTimeUtilities';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';
import { timeRangeFilterId, useWirelessController, wirelessControllerFilterId } from '../../filters';

const LOOPBACK_IP = '127.0.0.1';

const propertiesColumns = [
    {
        properties: [
            stringProperty(t`Name`, 'device.name'),
            stringProperty(t`IP Address`, 'device.ipAddress'),
            stringProperty(t`Location`, 'device.location'),
            linkProperty(t`Access Points`, 'apCounts.total', '/access-points', [], [], renderPageLink),
            linkProperty(t`Radios`, 'radioCounts.total', '/radios', [], [], renderPageLink),
            stringProperty(t`SSIDs`, 'ssidCounts.total'),
            listLinkProperty(t`Sites`, 'device.sites', [], [wirelessControllerFilterId], renderSiteList),
        ],
    },
    {
        properties: [
            stringProperty(t`Vendor`, 'device.vendor'),
            stringProperty(t`Model`, 'device.deviceModel'),
            stringProperty(t`Up Time`, 'uptime', getUpTimeDuration),
            stringProperty(t`MAC Address`, 'device.macAddress'),
            stringProperty(t`Serial Number`, 'device.serialNumber'),
            stringProperty(t`Software Version`, 'device.swVersion'),
            stringProperty(t`Hardware Version`, 'device.hwVersion'),
        ],
    },
    {
        properties: [listProperty(t`IP Addresses`, 'device.ipAddresses', renderIpAddrList)],
    },
];

function getTableRequestParams(uuid, start, end) {
    return {
        type: 'device',
        wirelesscontroller: uuid,
        renderAsEntity: true,
        start,
        end,
        entityFields: [
            'name',
            'ipAddress',
            'vendor',
            'deviceModel',
            'swVersion',
            'hwVersion',
            'macAddress',
            'serialNumber',
            'sites',
        ],
        metrics: ['ssidCounts', 'apCounts', 'radioCounts', 'rrmRunTime', 'uptime'],
    };
}

function usePropertiesData() {
    const wlc = useWirelessController();
    const { uuid } = wlc || {};
    const dataService = useDataService();
    const [props, setProps] = useState();
    const [timeRangeFilterState] = useCurrentFilterStateById(timeRangeFilterId);
    const { start, end } = timeRangeFilterState || {};
    const startTime = start ? start.getTime() : null;
    const endTime = end ? end.getTime() : null;

    useEffect(() => {
        if (uuid && startTime) {
            const tableRequest = dataService.get('/query/table', getTableRequestParams(uuid, startTime, endTime));
            const deviceRequest = dataService.get(`/ngp/v2/devices/${uuid}`);

            Promise.all([tableRequest, deviceRequest]).then(([tableData, deviceData]) => {
                setProps(processData(tableData?.data?.[0], deviceData));
            });
        }
    }, [dataService, uuid, startTime, endTime]);

    return props;
}

/**
 * Convert deviceData ipAddresses/loopbackIps into an array of objects suitable for PropertiesIpAddressList,
 * and remove the localhost loopback from the list if present. Set this in the tableData, and return the
 * combined set of properties.
 * @param tableData
 * @param deviceData
 * @return {*}
 */
function processData(tableData, deviceData) {
    if (tableData && deviceData) {
        const loopbacks = deviceData.loopbackIps || [];
        tableData.device.ipAddresses = _.map(_.pull(deviceData.ipAddresses || [], LOOPBACK_IP), (ip) => {
            return { ipAddress: ip, loopback: loopbacks.indexOf(ip) >= 0 };
        });
        return tableData;
    }

    return null;
}

export function WirelessControllerProperties({ data }) {
    const classes = useStyles();
    return (
        <div className={classes.propertiesPanel}>
            {propertiesColumns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function WirelessControllerPropertiesPanel() {
    const chartData = usePropertiesData();

    if (!chartData) return null;

    return <WirelessControllerProperties data={chartData} />;
}

WirelessControllerPropertiesPanel.propTypes = panelPropTypes;

WirelessControllerPropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Wireless Controller Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 200,
};

export default Panel({
    typeAlias: 'WirelessControllerAttributes',
    filters: {
        required: [timeRangeFilterId, wirelessControllerFilterId],
        optional: [],
    },
})(WirelessControllerPropertiesPanel);
